import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { 
  getSupplierGroups, 
  createSupplierGroup, 
  updateSupplierGroup, 
  deleteSupplierGroup 
} from '../../../models/suppliersModel';
import { toast } from 'react-toastify';

// Debounced Input for Search
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, onChange, debounce]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}

function SupplierGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialPagination = () => {
    const searchParams = new URLSearchParams(location.search);
    const page = parseInt(searchParams.get('page')) || 1;
    const pageSize = parseInt(searchParams.get('pageSize')) || 20;
    return { pageIndex: page - 1, pageSize };
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    Supplier_Group_Code: '',
    Supplier_Group_Name: '',
    Status: 1,
    Requires_COIDA: 0
  });

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Fetching supplier groups...');
      
      const params = {};
      
      // Add search filter if present
      if (globalFilter) {
        params.search = globalFilter;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
       
      console.log('API params:', params);
      const response = await getSupplierGroups(params);
      console.log('API response:', response);
      
      if (response && response.status === 'success' && response.data) {
        const allGroups = response.data || [];
        console.log('All groups received:', allGroups.length, allGroups);
        
        // Client-side filtering for search
        let filteredGroups = allGroups;
        if (globalFilter) {
          const searchLower = globalFilter.toLowerCase();
          filteredGroups = allGroups.filter(group => 
            (group.Supplier_Group_Name && group.Supplier_Group_Name.toLowerCase().includes(searchLower)) ||
            (group.Supplier_Group_Code && group.Supplier_Group_Code.toLowerCase().includes(searchLower)) ||
            (group.Description && group.Description.toLowerCase().includes(searchLower)) ||
            (group.Supplier_Group_Description && group.Supplier_Group_Description.toLowerCase().includes(searchLower))
          );
        }
        
        // Client-side pagination
        const startIndex = pagination.pageIndex * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const paginatedGroups = filteredGroups.slice(startIndex, endIndex);
        
        console.log('Setting groups:', paginatedGroups.length, 'of', filteredGroups.length);
        setGroups(paginatedGroups);
        setRowCount(filteredGroups.length);
      } else {
        console.log('No data in response or invalid response structure');
        setGroups([]);
        setRowCount(0);
        toast.info('No supplier groups found');
      }
    } catch (error) {
      console.error('Error fetching supplier groups:', error);
      toast.error(`Failed to load supplier groups: ${error.message}`);
      setGroups([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, statusFilter]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleRefresh = () => {
    fetchGroups();
  };

  const handleCreate = () => {
    setFormData({
      Supplier_Group_Code: '',
      Supplier_Group_Name: '',
      Status: 1,
      Requires_COIDA: 0
    });
    setShowCreateModal(true);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      Supplier_Group_Code: group.Supplier_Group_Code,
      Supplier_Group_Name: group.Supplier_Group_Name,
      Status: group.Status ?? 1,
      Requires_COIDA: group.Requires_COIDA ?? 0
    });
    setShowEditModal(true);
  };

  const handleDelete = async (code, name) => {
    if (window.confirm(`Are you sure you want to delete the supplier group "${name}"? This may affect suppliers assigned to this group.`)) {
      try {
        const response = await deleteSupplierGroup(code);
        if (response.status === 'success') {
          toast.success(response.message || 'Supplier group deleted successfully');
          fetchGroups();
        } else {
          toast.error(response.message || 'Failed to delete supplier group');
        }
      } catch (error) {
        console.error('Error deleting supplier group:', error);
        toast.error(error.response?.data?.message || 'An error occurred while deleting supplier group');
      }
    }
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await createSupplierGroup(formData);
      if (response.status === 'success') {
        toast.success('Supplier group created successfully');
        setShowCreateModal(false);
        fetchGroups();
      } else {
        toast.error(response.message || 'Failed to create supplier group');
      }
    } catch (error) {
      console.error('Error creating supplier group:', error);
      toast.error(error.response?.data?.message || 'An error occurred while creating supplier group');
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    try {
      const response = await updateSupplierGroup(editingGroup.Supplier_Group_Code, formData);
      if (response.status === 'success') {
        toast.success('Supplier group updated successfully');
        setShowEditModal(false);
        setEditingGroup(null);
        fetchGroups();
      } else {
        toast.error(response.message || 'Failed to update supplier group');
      }
    } catch (error) {
      console.error('Error updating supplier group:', error);
      toast.error(error.response?.data?.message || 'An error occurred while updating supplier group');
    }
  };

  const handlePageChange = (newPageIndex) => {
    setPagination(prev => ({ ...prev, pageIndex: newPageIndex }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination({ pageIndex: 0, pageSize: newPageSize });
  };

  const totalPages = Math.ceil(rowCount / pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;

  // Pagination component matching the exact ESR pattern
  const PaginationControls = () => (
    <div className="flex items-center justify-center py-4 bg-white">
      <div className="flex items-center space-x-1">
        <button
          onClick={() => handlePageChange(0)}
          disabled={currentPage === 1}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => handlePageChange(pagination.pageIndex - 1)}
          disabled={currentPage === 1}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <span className="flex items-center gap-1 text-sm text-gray-600 px-4">
          Page{' '}
          <strong>
            {pagination.pageIndex + 1} of{' '}{totalPages}
          </strong>
        </span>
        
        <button
          onClick={() => handlePageChange(pagination.pageIndex + 1)}
          disabled={currentPage === totalPages}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          onClick={() => handlePageChange(totalPages - 1)}
          disabled={currentPage === totalPages}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-2 py-1">
      {/* Professional Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between">
          {/* Left: Icon, Title, Description, Results */}
          <div className="flex items-start">
            <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <svg className="h-5 w-5" fill="none" stroke="#0096DE" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Supplier Groups</h1>
              <p className="text-xs text-gray-500 mb-1">Manage supplier group classifications</p>
              <div className="flex items-center mt-1">
                <span className="text-xs font-medium" style={{ color: '#0096DE' }}>
                  {rowCount ? `Showing ${Math.min(pagination.pageSize, rowCount)} of ${rowCount} results` : '0 results'}
                  {(globalFilter || statusFilter) && (
                    <span className="ml-1 text-blue-600">(filtered)</span>
                  )}
                </span>
              </div>
            </div>
          </div>
          
          {/* Right: All Controls and Search */}
          <div className="flex flex-col gap-2 items-end min-w-0">
            {/* Row 1: All Controls on Right */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Create Button */}
              {can && can('Import Suppliers') && (
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex-shrink-0"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Group
                </button>
              )}
              
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                title="Refresh"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0096DE' : 'currentColor'} style={{ color: loading ? '#0096DE' : 'currentColor' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              
              {/* Status Filter */}
              <div className="flex-shrink-0">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={`text-sm px-2 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    statusFilter ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white'
                  }`}
                >
                  <option value="">All Groups</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Page Size Filter */}
              <div className="flex-shrink-0">
                <select
                  value={pagination.pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>
            
            {/* Search Box in Row 2 */}
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <DebouncedInput
                value={globalFilter ?? ''}
                onChange={value => setGlobalFilter(String(value))}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search groups..."
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Supplier Groups Grid */}
      {loading ? (
        <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
          <div className="flex justify-center items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading supplier groups...</span>
          </div>
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
          <p className="text-gray-500">No supplier groups found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
          {groups.map((group) => {
            return (
              <div key={group.Supplier_Group_Code} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow hover:border-blue-200 transition-all duration-200 group flex flex-col h-full">
                {/* Header with Group Name */}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg text-blue-600 truncate">
                    {group.Supplier_Group_Name || group.Supplier_Group_Code || 'Unnamed Group'}
                  </h3>
                  {group.Requires_COIDA === 1 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700 flex-shrink-0">
                      COIDA
                    </span>
                  )}
                </div>

                {/* Flexible content area */}
                <div className="flex-1 mb-2">
                  {/* Compact Description */}
                  <div className="space-y-0.5 text-xs text-gray-600">
                    <div className="flex items-start">
                      <svg className="w-2.5 h-2.5 mr-1 mt-0.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="line-clamp-3">
                        {group.Description || group.Supplier_Group_Description || 'No description available'}
                      </span>
                    </div>
                    
                    <div className="flex items-center">
                      <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-2-5h6m-6 0V9m0 0h6m-6 0V5a2 2 0 012-2h4a2 2 0 012 2v4" />
                      </svg>
                      <span className="truncate">Supplier Classification</span>
                    </div>

                    <div className="flex items-center">
                      <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span>Code: {group.Supplier_Group_Code}</span>
                      <span className={`ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        (group.Status ?? 1) === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {(group.Status ?? 1) === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {can && can('Import Suppliers') && (
                  <div className="flex items-center gap-1 pt-2 border-t border-gray-100 mt-auto">
                    <button
                      onClick={() => handleEdit(group)}
                      className="flex-1 inline-flex items-center justify-center px-2 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                      title="Edit"
                    >
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(group.Supplier_Group_Code, group.Supplier_Group_Name)}
                      className="flex-1 inline-flex items-center justify-center px-2 py-1.5 border border-red-300 bg-white hover:bg-red-50 text-red-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                      title="Delete"
                    >
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Pagination */}
      {!loading && groups.length > 0 && <PaginationControls />}
      
      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <form onSubmit={handleSubmitCreate}>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Create Supplier Group</h3>
              </div>
              
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.Supplier_Group_Code}
                    onChange={(e) => setFormData({ ...formData, Supplier_Group_Code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., LIFTING_EQUIPMENT"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.Supplier_Group_Name}
                    onChange={(e) => setFormData({ ...formData, Supplier_Group_Name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Lifting Equipment"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.Status}
                    onChange={(e) => setFormData({ ...formData, Status: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    COIDA Required
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="requires-coida-create"
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={!!formData.Requires_COIDA}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          Requires_COIDA: e.target.checked ? 1 : 0
                        })
                      }
                    />
                    <label
                      htmlFor="requires-coida-create"
                      className="text-sm text-gray-700"
                    >
                      Supplier in this group must provide a COIDA certificate
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Modal */}
      {showEditModal && editingGroup && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <form onSubmit={handleSubmitEdit}>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Edit Supplier Group</h3>
              </div>
              
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Code
                  </label>
                  <input
                    type="text"
                    disabled
                    value={formData.Supplier_Group_Code}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">Code cannot be changed</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.Supplier_Group_Name}
                    onChange={(e) => setFormData({ ...formData, Supplier_Group_Name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Lifting Equipment"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.Status}
                    onChange={(e) => setFormData({ ...formData, Status: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    COIDA Required
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      id="requires-coida-edit"
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={!!formData.Requires_COIDA}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          Requires_COIDA: e.target.checked ? 1 : 0
                        })
                      }
                    />
                    <label
                      htmlFor="requires-coida-edit"
                      className="text-sm text-gray-700"
                    >
                      Supplier in this group must provide a COIDA certificate
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingGroup(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupplierGroups;
